import { UnorderedListOutlined } from "@ant-design/icons";
import { Button, Select, Tooltip } from "antd";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import UnifiedDynamicForm from "../../../components/VMRS/ExploreTasking/components/form/UnifiedDynamicForm";
import { checkTypeImageIncludeProvider } from "./ultis";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";

interface SatelliteSearchPanelProps
{
   imageType: any;
   setImageType: (val: any) => void;
   handleSearch: () => void;
   showBackToResultSearch: boolean;
   setShowResultSearch: (val: boolean) => void;
   providers: any;
   imageTypes: any[];
   formConfig?: any;
   formData: Record<string, any>;
   setFormData: (data: any) => void;
   setProviders: (providers: any) => void;
   setProviderCode: (providerCode: any) => void;
   setProviderId: (providerId: any) => void;
   /** Set when the active AOI is outside the image type's area bounds. */
   aoiAreaError?: string;
}

export default function SatelliteSearchPanel(props: SatelliteSearchPanelProps)
{
   const { t } = useTranslation();

   const {
      imageType,
      setImageType,
      handleSearch,
      showBackToResultSearch,
      setShowResultSearch,
      providers,
      imageTypes,
      formConfig,
      formData,
      setFormData,
      setProviders,
      setProviderCode,
      setProviderId,
      aoiAreaError,
   } = props;

   // Prepare step object for UnifiedDynamicForm if formConfig is available
   const step = formConfig
      ? {
         ui: formConfig.ui_schema?.ui,
      }
      : null;

   const imageTypeOptions = useMemo(
      () => imageTypes?.map((item: any) => ({
         label: item.label,
         value: item.value,
         disabled: checkTypeImageIncludeProvider(item)?.length === 0,
      })) ?? [],
      [imageTypes],
   );

   const allAOI = useSelector((state: RootState) => state.aoi.features);

   // Validate required fields declared in the form schema (ui.options[key].required).
   // `geometry` is satisfied by the drawn AOI, so it's excluded here.
   const missingRequiredFields = useMemo(() =>
   {
      const ui = formConfig?.ui_schema?.ui;
      const order: string[] = ui?.order ?? [];
      const options: Record<string, any> = ui?.options ?? {};
      if (order.length === 0) return [];

      // Mirror UnifiedDynamicForm's dependency logic: skip fields hidden by a condition.
      const shouldRender = (key: string, config: any) =>
      {
         if (!config?.depends_on && !config?.depField) return true;
         const depField = config.depends_on?.field ?? config.depField;
         const depValue = config.depends_on?.value ?? key;
         if (!depField) return true;
         const controllerValue = formData[depField] ?? options[depField]?.default;
         return controllerValue === depValue;
      };

      const hasValue = (key: string, config: any) =>
      {
         const v = formData[key];
         const filled =
            v !== undefined &&
            v !== null &&
            v !== "" &&
            !(Array.isArray(v) && v.length === 0);
         if (filled) return true;
         // A field with a configured default is effectively pre-filled by the form.
         return (
            config?.default !== undefined ||
            config?.defaultMin !== undefined ||
            config?.defaultMax !== undefined
         );
      };

      return order
         .filter((key) => key !== "geometry")
         .filter((key) =>
         {
            const config = options[key];
            return (
               config?.required && shouldRender(key, config) && !hasValue(key, config)
            );
         })
         .map((key) => t(options[key]?.title || options[key]?.label || key));
   }, [formConfig, formData, t]);

   const tooltipTitle =
      allAOI?.length === 0
         ? t("Please draw an area of interest.")
         : aoiAreaError
            ? aoiAreaError
            : (!providers || providers.length === 0)
               ? t("No providers available for the selected image type.")
               : missingRequiredFields.length > 0
                  ? `${t("Please fill in all required fields")}: ${missingRequiredFields.join(", ")}`
                  : undefined;



   return (
      <div className="w-full h-full flex flex-col !bg-[var(--neutral-1000)] text-[var(--color-main-text)]">
         {/* Title - fixed header */}
         <div className="sat-search-header flex justify-between items-center gap-3 px-6 pt-5 pb-4 flex-shrink-0">
            <span className="min-w-0 text-lg font-semibold">
               {t("Search & Order Satellite Images")}
            </span>

            {showBackToResultSearch && (
               <Tooltip title={t("Back to search result")}>
                  <div
                     role="button"
                     aria-label={t("Back to search result")}
                     className="back-to-result !bg-[var(--neutral-950)] cursor-pointer rounded-lg text-[var(--color-main-text)] px-3 py-2 h-8 flex gap-2 items-center flex-shrink-0 whitespace-nowrap"
                     onClick={() => setShowResultSearch(true)}
                  >
                     <UnorderedListOutlined className="icon-inherit" />
                     <span className="back-label">{t("Back to search result")}</span>
                  </div>
               </Tooltip>
            )}
         </div>

         {/* Scrollable body */}
         <div className="flex-1 overflow-auto min-h-0">
            {/* Image Type */}
            <div className="mb-5 px-6">
               <p className="text-sm mb-3 text-gray-300">{t("Image Type")}</p>

               {imageTypes?.length > 6 ? (
                  <Select
                     className="w-full div-dropdown"
                     value={imageType}
                     virtual={false}
                     showSearch
                     filterOption={(input, option) =>
                        (option?.label as string ?? "").toLowerCase().includes(input.toLowerCase())
                     }
                     onChange={(val: string) =>
                     {
                        const item = imageTypes.find((i: any) => i.value === val);
                        if (!item || checkTypeImageIncludeProvider(item)?.length === 0) return;
                        setImageType(item.value);
                        setProviders(checkTypeImageIncludeProvider(item));
                        setProviderCode(checkTypeImageIncludeProvider(item)?.[0]?.code);
                        setProviderId(checkTypeImageIncludeProvider(item)?.[0]?.id);
                     }}
                     options={imageTypeOptions}
                  />
               ) : (
                  // 2 or 4 types fill a 2-column grid evenly; 3, 5 or 6 fit 3 columns.
                  <div
                     className={
                        "grid gap-2 " +
                        (imageTypes?.length === 1
                           ? "grid-cols-1"
                           : imageTypes?.length === 2 || imageTypes?.length === 4
                              ? "grid-cols-2"
                              : "grid-cols-3")
                     }
                  >
                     {imageTypes?.map((item: any, index: number) => (
                        <Tooltip
                           key={index}
                           title={
                              checkTypeImageIncludeProvider(item)?.length > 0
                                 ? ""
                                 : t("There is currently no provider that offers this type of photo.")
                           }
                        >
                           <div
                              onClick={() =>
                              {
                                 if (checkTypeImageIncludeProvider(item)?.length > 0)
                                 {
                                    setImageType(item?.value);
                                    setProviders(checkTypeImageIncludeProvider(item));
                                    setProviderCode(checkTypeImageIncludeProvider(item)?.[0]?.code);
                                    setProviderId(checkTypeImageIncludeProvider(item)?.[0]?.id);
                                 }
                              }}
                              className={
                                 "div-type-image " +
                                 (imageType === item?.value ? "div-type-image-active" : "")
                              }
                              style={{
                                 cursor:
                                    checkTypeImageIncludeProvider(item)?.length > 0
                                       ? "pointer"
                                       : "not-allowed",
                              }}
                           >
                              {item?.label}
                           </div>
                        </Tooltip>
                     ))}
                  </div>
               )}
            </div>

            {/* Dynamic Form or Hardcoded Fallback */}
            {formConfig && step && (
               <UnifiedDynamicForm
                  step={step}
                  formData={formData}
                  setFormData={setFormData}
                  type="add"
                  display="col"
                  disablePastDates={false} // archive: only past dates allowed
               />
            )}
         </div>

         {/* Button - fixed footer */}
         <div
            className="flex w-full justify-end px-6 py-4 flex-shrink-0"
            style={{ borderTop: "1px solid var(--divider-1)" }}
         >
            <span className="w-[151px] block">
               <Tooltip
                  title={tooltipTitle
                  }
               >
                  <Button
                     onClick={() =>
                     {
                        if (!tooltipTitle) handleSearch();
                     }}
                     // disabled={false}
                     className={`w-full !border-none ${tooltipTitle
                        ? "opacity-50 cursor-not-allowed"
                        : "!bg-[var(--primary-4)] text-[var(--primary-950)]"
                        }`}
                  >
                     {t("Search Images")}
                  </Button>
               </Tooltip>
            </span>
         </div>
      </div>
   );
}
